<template>
  <canvas ref="canvas" class="code-canvas"></canvas>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const canvas = ref(null)
let ctx = null
let animationId = null
let columns = []
let mouse = { x: -1000, y: -1000 }
const revealRadius = 200

// 코드 문자들
const codeChars = '01{}[]()<>/*-+=;:,.?!@#$%^&|~ABCDEFabcdef'

// Mocha Mousse 컬러 팔레트
const colors = {
  primary: '#a47864',    // Mocha Mousse
  secondary: '#8ba475',  // Tendrill
  tertiary: '#d19c97',   // Rose Tan
  accent: '#7391C8'      // Cornflower Blue
}

class CodeColumn {
  constructor(x, canvasHeight, fontSize) {
    this.x = x
    this.fontSize = fontSize
    this.canvasHeight = canvasHeight
    this.chars = []
    this.speed = 0.3 + Math.random() * 0.5
    this.initChars()
  }

  initChars() {
    const charCount = Math.ceil(this.canvasHeight / this.fontSize) + 5
    for (let i = 0; i < charCount; i++) {
      this.chars.push({
        y: i * this.fontSize - this.canvasHeight * Math.random(),
        char: codeChars[Math.floor(Math.random() * codeChars.length)],
        changeTimer: Math.random() * 100
      })
    }
  }

  update() {
    this.chars.forEach(char => {
      char.y += this.speed
      char.changeTimer--

      if (char.changeTimer <= 0) {
        char.char = codeChars[Math.floor(Math.random() * codeChars.length)]
        char.changeTimer = 50 + Math.random() * 100
      }

      if (char.y > this.canvasHeight + this.fontSize) {
        char.y = -this.fontSize
      }
    })
  }

  draw(ctx, mouseX, mouseY, revealRadius) {
    this.chars.forEach(char => {
      const dx = this.x - mouseX
      const dy = char.y - mouseY
      const distance = Math.sqrt(dx * dx + dy * dy)

      if (distance < revealRadius) {
        const opacity = 1 - (distance / revealRadius)
        const colorIndex = Math.floor(Math.random() * 10)

        let color
        if (colorIndex < 6) {
          color = colors.primary
        } else if (colorIndex < 8) {
          color = colors.secondary
        } else {
          color = colors.tertiary
        }

        ctx.fillStyle = color
        ctx.globalAlpha = opacity * 0.8
        ctx.font = `${this.fontSize}px 'Fira Code', 'Consolas', monospace`
        ctx.fillText(char.char, this.x, char.y)
        ctx.globalAlpha = 1
      }
    })
  }
}

function init() {
  if (!canvas.value) return false

  ctx = canvas.value.getContext('2d')
  if (!ctx) return false

  resize()
  return true
}

function resize() {
  if (!canvas.value || !ctx) return

  const dpr = Math.min(window.devicePixelRatio, 2)
  canvas.value.width = window.innerWidth * dpr
  canvas.value.height = window.innerHeight * dpr
  canvas.value.style.width = window.innerWidth + 'px'
  canvas.value.style.height = window.innerHeight + 'px'
  ctx.scale(dpr, dpr)

  // 컬럼 재생성
  columns = []
  const fontSize = 14
  const columnSpacing = 20
  const columnCount = Math.ceil(window.innerWidth / columnSpacing)

  for (let i = 0; i < columnCount; i++) {
    columns.push(new CodeColumn(i * columnSpacing, window.innerHeight, fontSize))
  }
}

function animate() {
  if (!ctx || !canvas.value) return

  // 검정 배경으로 클리어
  ctx.fillStyle = '#0a0a0a'
  ctx.fillRect(0, 0, window.innerWidth, window.innerHeight)

  // 컬럼 업데이트 및 그리기
  columns.forEach(column => {
    column.update()
    column.draw(ctx, mouse.x, mouse.y, revealRadius)
  })

  // 마우스 주변 글로우 효과
  if (mouse.x > 0 && mouse.y > 0) {
    const gradient = ctx.createRadialGradient(
      mouse.x, mouse.y, 0,
      mouse.x, mouse.y, revealRadius
    )
    gradient.addColorStop(0, 'rgba(164, 120, 100, 0.1)')
    gradient.addColorStop(0.5, 'rgba(164, 120, 100, 0.05)')
    gradient.addColorStop(1, 'rgba(164, 120, 100, 0)')

    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, window.innerWidth, window.innerHeight)
  }

  animationId = requestAnimationFrame(animate)
}

function handleMouseMove(e) {
  mouse.x = e.clientX
  mouse.y = e.clientY
}

function handleMouseLeave() {
  mouse.x = -1000
  mouse.y = -1000
}

function handleTouchMove(e) {
  if (e.touches.length > 0) {
    mouse.x = e.touches[0].clientX
    mouse.y = e.touches[0].clientY
  }
}

function handleTouchEnd() {
  mouse.x = -1000
  mouse.y = -1000
}

onMounted(() => {
  if (init()) {
    animate()
    window.addEventListener('resize', resize)
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseleave', handleMouseLeave)
    window.addEventListener('touchmove', handleTouchMove)
    window.addEventListener('touchend', handleTouchEnd)
  }
})

onUnmounted(() => {
  if (animationId) {
    cancelAnimationFrame(animationId)
  }
  window.removeEventListener('resize', resize)
  window.removeEventListener('mousemove', handleMouseMove)
  window.removeEventListener('mouseleave', handleMouseLeave)
  window.removeEventListener('touchmove', handleTouchMove)
  window.removeEventListener('touchend', handleTouchEnd)
})
</script>

<style scoped>
.code-canvas {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}
</style>
